<script setup lang="ts">
// Wspólny blok stałych kodów rabatowych „Im więcej kupujesz, tym więcej zyskujesz".
// Koszyk: klik = zastosuj kod (rodzic obsługuje @select). Karta produktu: copyOnClick
// -> klik kopiuje kod do schowka i pokazuje krótko „Skopiowano!".
//
// Kody, progi, data startu i kolory panelu są PER MARKA (app/brands/<brand>/config.ts):
// lavmag = EXTRA* na żółtym, youlook = MORE* na zielonym. Marka bez sekcji
// `standingPromo` nie prowadzi akcji i komponent nic nie renderuje.
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    copyOnClick?: boolean;
    title?: string;
  }>(),
  {
    disabled: false,
    copyOnClick: false,
    title: "Im więcej kupujesz, tym więcej zyskujesz:",
  },
);

const emit = defineEmits<{ select: [code: string] }>();

const { config, isActive, bestCode } = useStandingPromo();

// Kolory jako custom properties — jedno miejsce na paletę akcji, bez wariantów
// per brand w CSS (belka i popup mają własne kolory, bo idą z PrestaShop).
const accentStyle = computed(() => {
  const accent = config.value?.accent;
  if (!accent) return {};
  return {
    "--standing-codes-bg": accent.bg,
    "--standing-codes-text": accent.text,
    "--standing-codes-best": accent.best,
  } as Record<string, string>;
});

const copiedCode = ref<string | null>(null);
let copyTimer: ReturnType<typeof setTimeout> | null = null;

async function onSelect(code: string) {
  if (props.disabled) return;
  emit("select", code);
  if (props.copyOnClick && import.meta.client) {
    try {
      await navigator.clipboard.writeText(code);
      copiedCode.value = code;
      if (copyTimer) clearTimeout(copyTimer);
      copyTimer = setTimeout(() => {
        copiedCode.value = null;
      }, 1800);
    } catch {
      // schowek niedostępny — pomijamy
    }
  }
}

onBeforeUnmount(() => {
  if (copyTimer) clearTimeout(copyTimer);
});
</script>

<template>
  <div v-if="isActive && config" class="standing-codes" :style="accentStyle">
    <p class="standing-codes__title">{{ title }}</p>
    <ul class="standing-codes__list">
      <li v-for="ec in config.codes" :key="ec.code">
        <button
          type="button"
          class="standing-codes__item"
          :class="{ 'standing-codes__item--best': ec.code === bestCode }"
          :disabled="disabled"
          :aria-label="`${copyOnClick ? 'Skopiuj' : 'Zastosuj'} kod ${ec.code}`"
          @click="onSelect(ec.code)"
        >
          <span class="standing-codes__label"
            >Rabat <strong>{{ ec.pct }}</strong> przy zakupie od
            {{ ec.from }}</span
          >
          <span class="standing-codes__code">
            <template v-if="copiedCode === ec.code">Skopiowano!</template>
            <template v-else>{{ ec.code }}</template>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.standing-codes {
  padding: 12px 20px;
  background: var(--standing-codes-bg, #fff881);
  border-radius: var(--btn-radius, 8px);
}

.standing-codes__title {
  margin: 0 0 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--standing-codes-text, var(--color-black, #151515));
}

.standing-codes__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.standing-codes__item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 5px 8px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font: inherit;
  color: var(--standing-codes-text, var(--color-black, #151515));
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.standing-codes__item:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
}

.standing-codes__item:disabled {
  opacity: 0.55;
  cursor: default;
}

.standing-codes__label {
  font-size: 0.875rem;
}

.standing-codes__code {
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.standing-codes__item--best .standing-codes__label,
.standing-codes__item--best .standing-codes__code {
  color: var(--standing-codes-best, #e30613);
}
</style>
